import type {ComponentPropsWithoutRef} from 'react';
import {forwardRef} from 'react';
import {Slot} from '@radix-ui/react-slot';

import {cn} from '@/modules/foundation/shared/tailwind';

type HeadingProps = ComponentPropsWithoutRef<'h1'> & {
	/** Heading level. This will translate into any h1-6 level unless `asChild` is defined */
	level?: 1 | 2 | 3 | 4 | 5 | 6;
	/** Changes text sizing
	 * @default 'xlarge'
	 */
	size?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'small';
	/** Adds margin-bottom */
	spacing?: boolean;
	/**
	 * Change the default rendered element for the one passed as a child, merging their props and behavior.
	 * @default false
	 */
	asChild?: boolean;
};

/** Use `Heading` to render h1-6 elements with heading text styles.  */
export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(
	({level = 1, size, spacing = false, className, asChild, ...rest}, ref) => {
		const Component = asChild ? Slot : `h${level ?? 1}`;

		let defaultSize: HeadingProps['size'] = 'h1';
		switch (level) {
			case 1:
				defaultSize = 'h1';
				break;
			case 2:
				defaultSize = 'h2';
				break;
			case 3:
				defaultSize = 'h3';
				break;
			case 4:
				defaultSize = 'h4';
				break;
			case 5:
				defaultSize = 'h5';
				break;
			case 6:
				defaultSize = 'h6';
				break;
		}

		size = size ?? defaultSize;

		return (
			<Component
				ref={ref}
				className={cn('font-bold', 'font-display', [
					size === 'h1' && [
						'text-4xl',
						'leading-tight',
						'md:text-5xl',
						'md:leading-tight',
					],
					size === 'h2' && [
						'text-[28px]',
						'leading-tight',
						'md:text-4xl',
						'md:leading-tight',
					],
					size === 'h3' && ['text-2xl', 'md:text-[28px]', 'leading-tight'],
					size === 'h4' && ['text-[22px]', 'leading-tight'],
					size === 'h5' && ['text-lg', 'font-sans', 'leading-tight'],
					size === 'h6' && ['text-base', 'font-sans', 'leading-tight'],
					size === 'small' && ['text-base', 'font-sans', 'leading-tight'],
					spacing && [
						size === 'h1' && ['mb-3'],
						size === 'h2' && ['mb-3'],
						size === 'h3' && ['mb-3'],
						size === 'h4' && ['mb-3'],
						size === 'h5' && ['mb-3'],
						size === 'h6' && ['mb-3'],
					],
					className,
				])}
				{...rest}
			/>
		);
	},
);

Heading.displayName = 'Heading';
